import { Brand, BrandSuggestion } from '../types';

export interface WorkspaceItem {
  id: string;
  name: string;
  domain: string;
  initials: string;
  plan: string;
}

export const PEEC_DB_NAME = 'peec-ai-local-db';
export const PEEC_DB_VERSION = 1;

export type DatasetName =
  | 'overview'
  | 'prompts'
  | 'domains'
  | 'urls'
  | 'gapAnalysis'
  | 'insights'
  | 'earned'
  | 'owned'
  | 'impact'
  | 'profile'
  | 'brandProfile'
  | 'earnedRecommendations'
  | 'ownedRecommendations'
  | 'domainMovers'
  | 'domainTypes'
  | 'llmPlatforms';

export interface DatasetRecord<T = unknown> {
  key: string;
  workspaceId: string;
  name: DatasetName;
  value: T;
  updated_at: string;
}

export interface StoredBrand extends Brand {
  workspaceId: string;
  created_at: string;
  updated_at: string;
}

export interface StoredBrandSuggestion extends BrandSuggestion {
  workspaceId: string;
  created_at: string;
  updated_at: string;
}

export interface AISearchRecord {
  id: string;
  workspaceId: string;
  prompt: string;
  ai: string;
  domain: string;
  domain_type: string;
  brand: string;
  brand_list: string[];
  title: string;
  response_json: Record<string, unknown>;
  url_list: string[];
  url_type_list: string[];
  timestamp: string;
}

type StoreName = 'meta' | 'datasets' | 'workspaces' | 'brands' | 'brandSuggestions' | 'aiSearchRecords';

let databasePromise: Promise<IDBDatabase> | null = null;

const createStore = (db: IDBDatabase, name: StoreName, options?: IDBObjectStoreParameters) => {
  if (!db.objectStoreNames.contains(name)) {
    return db.createObjectStore(name, options);
  }

  return null;
};

export function openPeecDB(): Promise<IDBDatabase> {
  if (databasePromise) {
    return databasePromise;
  }

  databasePromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = indexedDB.open(PEEC_DB_NAME, PEEC_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      createStore(db, 'meta', { keyPath: 'key' });

      const datasets = createStore(db, 'datasets', { keyPath: 'key' });
      datasets?.createIndex('workspaceId', 'workspaceId', { unique: false });
      datasets?.createIndex('name', 'name', { unique: false });

      createStore(db, 'workspaces', { keyPath: 'id' });

      const brands = createStore(db, 'brands', { keyPath: 'id' });
      brands?.createIndex('workspaceId', 'workspaceId', { unique: false });
      brands?.createIndex('domains', 'domains', { unique: false, multiEntry: true });
      brands?.createIndex('tracked_names', 'tracked_names', { unique: false, multiEntry: true });

      const suggestions = createStore(db, 'brandSuggestions', { keyPath: 'id' });
      suggestions?.createIndex('workspaceId', 'workspaceId', { unique: false });
      suggestions?.createIndex('status', 'status', { unique: false });

      const records = createStore(db, 'aiSearchRecords', { keyPath: 'id' });
      records?.createIndex('workspaceId', 'workspaceId', { unique: false });
      records?.createIndex('ai', 'ai', { unique: false });
      records?.createIndex('domain', 'domain', { unique: false });
      records?.createIndex('domain_type', 'domain_type', { unique: false });
      records?.createIndex('brand', 'brand', { unique: false });
      records?.createIndex('brand_list', 'brand_list', { unique: false, multiEntry: true });
      records?.createIndex('url_list', 'url_list', { unique: false, multiEntry: true });
      records?.createIndex('url_type_list', 'url_type_list', { unique: false, multiEntry: true });
      records?.createIndex('timestamp', 'timestamp', { unique: false });
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return databasePromise;
}

const transactionDone = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onerror = () => reject(transaction.error);
  transaction.onabort = () => reject(transaction.error);
});

export async function getAllFromStore<T>(storeName: StoreName): Promise<T[]> {
  const db = await openPeecDB();

  return new Promise((resolve, reject) => {
    const request = db.transaction(storeName, 'readonly').objectStore(storeName).getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

export async function getByKey<T>(storeName: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openPeecDB();

  return new Promise((resolve, reject) => {
    const request = db.transaction(storeName, 'readonly').objectStore(storeName).get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllByIndex<T>(
  storeName: StoreName,
  indexName: string,
  query: IDBValidKey | IDBKeyRange
): Promise<T[]> {
  const db = await openPeecDB();

  return new Promise((resolve, reject) => {
    const request = db.transaction(storeName, 'readonly').objectStore(storeName).index(indexName).getAll(query);
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

export async function putRecord<T>(storeName: StoreName, value: T): Promise<void> {
  const db = await openPeecDB();
  const transaction = db.transaction(storeName, 'readwrite');
  transaction.objectStore(storeName).put(value);
  await transactionDone(transaction);
}

export async function putRecords<T>(storeName: StoreName, values: T[]): Promise<void> {
  if (values.length === 0) {
    return;
  }

  const db = await openPeecDB();
  const transaction = db.transaction(storeName, 'readwrite');
  const store = transaction.objectStore(storeName);
  values.forEach((value) => store.put(value));
  await transactionDone(transaction);
}

export async function deleteRecord(storeName: StoreName, key: IDBValidKey): Promise<void> {
  const db = await openPeecDB();
  const transaction = db.transaction(storeName, 'readwrite');
  transaction.objectStore(storeName).delete(key);
  await transactionDone(transaction);
}

export const datasetKey = (workspaceId: string, name: DatasetName) => `${workspaceId}:${name}`;

export async function getDataset<T>(workspaceId: string, name: DatasetName): Promise<T | undefined> {
  const record = await getByKey<DatasetRecord<T>>('datasets', datasetKey(workspaceId, name));
  return record?.value;
}

export async function putDataset<T>(workspaceId: string, name: DatasetName, value: T): Promise<void> {
  await putRecord<DatasetRecord<T>>('datasets', {
    key: datasetKey(workspaceId, name),
    workspaceId,
    name,
    value,
    updated_at: new Date().toISOString(),
  });
}

export type { WorkspaceItem };